import React, { useEffect, useState } from "react";
import api, { apiErr } from "../lib/api";
import { PageHeader, fmtNum } from "../components/Shared";
import { toast } from "sonner";
import { Warehouse, Plus, Pencil, Trash2, Loader2, X, User, Boxes } from "lucide-react";

export default function Locations() {
  const [locs, setLocs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [modal, setModal] = useState(null); // null | {} | loc

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/locations");
      setLocs(data);
    } catch (e) { toast.error(apiErr(e.response?.data?.detail)); }
    finally { setLoading(false); }
  };

  useEffect(() => { load(); }, []);

  const del = async (loc) => {
    if (!window.confirm(`Hapus tempat "${loc.nama}"? Obat di dalamnya tidak ikut terhapus.`)) return;
    await api.delete(`/locations/${loc.id}`);
    toast.success("Tempat penyimpanan dihapus");
    load();
  };

  return (
    <div className="pb-12">
      <PageHeader title="Tempat Penyimpanan" subtitle="Kelola depo / gudang tempat menghitung obat">
        <button onClick={() => setModal({})} data-testid="add-location-button" className="inline-flex items-center gap-2 rounded-lg bg-teal-700 hover:bg-teal-800 px-3.5 py-2 text-sm font-semibold text-white transition-colors">
          <Plus className="h-4 w-4" /> Tambah Tempat
        </button>
      </PageHeader>

      <div className="px-6 lg:px-8 pt-6">
        {loading ? (
          <div className="text-center py-16 text-slate-400"><Loader2 className="h-5 w-5 animate-spin inline" /></div>
        ) : locs.length === 0 ? (
          <div className="bg-white rounded-xl border border-slate-200 py-16 text-center">
            <Warehouse className="h-10 w-10 text-slate-300 mx-auto mb-3" />
            <p className="text-slate-500 text-sm">Belum ada tempat penyimpanan. Tambahkan atau impor master obat.</p>
          </div>
        ) : (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
            {locs.map((l) => (
              <div key={l.id} className="bg-white rounded-xl border border-slate-200 p-5 hover:border-teal-300 hover:shadow-sm transition-all group" data-testid="location-card">
                <div className="flex items-start justify-between">
                  <div className="h-11 w-11 rounded-lg bg-teal-50 flex items-center justify-center">
                    <Warehouse className="h-5 w-5 text-teal-700" />
                  </div>
                  <div className="flex gap-1 opacity-0 group-hover:opacity-100 transition-opacity">
                    <button onClick={() => setModal(l)} className="p-1.5 rounded-md text-slate-400 hover:text-teal-700 hover:bg-teal-50" data-testid="edit-location-button"><Pencil className="h-4 w-4" /></button>
                    <button onClick={() => del(l)} className="p-1.5 rounded-md text-slate-400 hover:text-red-600 hover:bg-red-50" data-testid="delete-location-button"><Trash2 className="h-4 w-4" /></button>
                  </div>
                </div>
                <h3 className="mt-4 font-head font-bold text-slate-900">{l.nama}</h3>
                {l.gudang && <p className="text-xs text-slate-400 mt-0.5">{l.gudang}</p>}
                {l.penanggung_jawab && (
                  <p className="text-xs text-slate-500 mt-2 flex items-center gap-1.5"><User className="h-3.5 w-3.5" /> {l.penanggung_jawab}</p>
                )}
                {l.penanggung_jawab_2 && (
                  <p className="text-xs text-slate-500 mt-1 flex items-center gap-1.5"><User className="h-3.5 w-3.5" /> {l.penanggung_jawab_2}</p>
                )}
                <div className="mt-4 pt-4 border-t border-slate-100 flex items-center justify-between text-xs">
                  <span className="flex items-center gap-1.5 text-slate-600"><Boxes className="h-4 w-4 text-slate-400" /> {fmtNum(l.total_obat)} obat</span>
                  <span className="text-teal-700 font-semibold">{fmtNum(l.total_counted)} dihitung</span>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {modal !== null && <LocationModal loc={modal} onClose={() => setModal(null)} onSaved={() => { setModal(null); load(); }} />}
    </div>
  );
}

function LocationModal({ loc, onClose, onSaved }) {
  const isEdit = !!loc.id;
  const [f, setF] = useState({
    nama: loc.nama || "", kode: loc.kode || "", gudang: loc.gudang || "",
    penanggung_jawab: loc.penanggung_jawab || "", penanggung_jawab_2: loc.penanggung_jawab_2 || "",
    deskripsi: loc.deskripsi || "",
  });
  const [saving, setSaving] = useState(false);
  const set = (k, v) => setF({ ...f, [k]: v });

  const [gudangOpts, setGudangOpts] = useState([]);
  useEffect(() => {
    api.get("/gudang-list").then((r) => {
      setGudangOpts(r.data);
      if (!isEdit && !loc.gudang && r.data.length) setF((p) => ({ ...p, gudang: r.data[0] }));
    }).catch(() => {});
    // eslint-disable-next-line
  }, []);

  const save = async () => {
    if (!f.nama.trim()) { toast.error("Nama tempat wajib diisi"); return; }
    setSaving(true);
    try {
      if (isEdit) await api.put(`/locations/${loc.id}`, f);
      else await api.post("/locations", f);
      toast.success(isEdit ? "Tempat diperbarui" : "Tempat ditambahkan");
      onSaved();
    } catch (e) { toast.error(apiErr(e.response?.data?.detail)); }
    finally { setSaving(false); }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-slate-900/40 backdrop-blur-sm" onClick={onClose}>
      <div className="bg-white rounded-2xl shadow-xl w-full max-w-md p-6" onClick={(e) => e.stopPropagation()}>
        <div className="flex items-center justify-between mb-5">
          <h3 className="font-head text-lg font-bold text-slate-900">{isEdit ? "Edit Tempat" : "Tambah Tempat Penyimpanan"}</h3>
          <button onClick={onClose} className="text-slate-400 hover:text-slate-700"><X className="h-5 w-5" /></button>
        </div>
        <div className="space-y-3">
          <div>
            <label className="text-xs font-medium text-slate-600">Nama Tempat *</label>
            <input value={f.nama} onChange={(e) => set("nama", e.target.value)} data-testid="location-name-input" placeholder="cth: Depo IGD, Gudang Farmasi"
              className="mt-1 w-full rounded-lg border border-slate-300 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-teal-600/40 focus:border-teal-600" />
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="text-xs font-medium text-slate-600">Kode</label>
              <input value={f.kode} onChange={(e) => set("kode", e.target.value)} className="mt-1 w-full rounded-lg border border-slate-300 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-teal-600/40 focus:border-teal-600" />
            </div>
            <div>
              <label className="text-xs font-medium text-slate-600">Gudang</label>
              <select value={f.gudang} onChange={(e) => set("gudang", e.target.value)} data-testid="location-gudang-select"
                className="mt-1 w-full rounded-lg border border-slate-300 bg-white px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-teal-600/40 focus:border-teal-600">
                <option value="">— pilih gudang —</option>
                {gudangOpts.map((g) => <option key={g} value={g}>{g}</option>)}
                {f.gudang && !gudangOpts.includes(f.gudang) && <option value={f.gudang}>{f.gudang}</option>}
              </select>
            </div>
          </div>
          <div>
            <label className="text-xs font-medium text-slate-600">Penanggung Jawab 1</label>
            <input value={f.penanggung_jawab} onChange={(e) => set("penanggung_jawab", e.target.value)} data-testid="location-pj1-input" className="mt-1 w-full rounded-lg border border-slate-300 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-teal-600/40 focus:border-teal-600" />
          </div>
          <div>
            <label className="text-xs font-medium text-slate-600">Penanggung Jawab 2</label>
            <input value={f.penanggung_jawab_2} onChange={(e) => set("penanggung_jawab_2", e.target.value)} data-testid="location-pj2-input" className="mt-1 w-full rounded-lg border border-slate-300 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-teal-600/40 focus:border-teal-600" />
          </div>
          <div>
            <label className="text-xs font-medium text-slate-600">Deskripsi</label>
            <textarea value={f.deskripsi} onChange={(e) => set("deskripsi", e.target.value)} rows={2} className="mt-1 w-full rounded-lg border border-slate-300 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-teal-600/40 focus:border-teal-600" />
          </div>
        </div>
        <div className="mt-6 flex justify-end gap-2">
          <button onClick={onClose} className="px-4 py-2 rounded-lg border border-slate-300 text-sm font-medium text-slate-700 hover:bg-slate-50">Batal</button>
          <button onClick={save} disabled={saving} data-testid="save-location-button" className="inline-flex items-center gap-2 px-4 py-2 rounded-lg bg-teal-700 hover:bg-teal-800 text-white text-sm font-semibold disabled:opacity-60">
            {saving && <Loader2 className="h-4 w-4 animate-spin" />} Simpan
          </button>
        </div>
      </div>
    </div>
  );
}
